import React, { useEffect, useMemo, useState } from "react";
import { CircularProgress } from "@mui/material";
import { notification } from "antd";
import { connect, useDispatch } from "react-redux";
import { formValueSelector, reduxForm } from "redux-form";

import {
  generateJobShiftEmergencyReadinessPdf,
  saveJobShiftEmergencyReadiness
} from "../../../../../actions/v2/JobShiftEmergencyReadinessActions";
import { downloadDocument } from "../../../../../utils/common";
import { Strings } from "../../../../../dataProvider/localize";
import styles from "../JobShiftPreStart/JobShiftPreStartView.module.css";
import JobShiftReadinessViewHeader from "./JobShiftReadinessViewHeader";
import ReadinessDetails from "./sections/ReadinessDetails";
import ReadinessQuestionsBlock from "./sections/ReadinessQuestionsBlock";
import ReadinessFlaggedIncorrectiveQuestions from "./sections/ReadinessFlaggedIncorrectiveQuestions";
import ReadinessSignOff from "./sections/ReadinessSignOff";
import { buildPreStartQuestionEntries } from "../JobShiftPreStart/jobShiftPreStartFormUtils";
import { formatAnswerValue, resolveParticipantName } from "../JobShiftPreStart/jobShiftPreStartUtils";
import PreStartPhotoPreviewModal from "../JobShiftPreStart/PreStartPhotoPreviewModal";
import ReadinessHeadingEditorModal from "./ReadinessHeadingEditorModal";
import {
  buildReadinessSavePayload,
  getReadinessChecklist,
  hasBlockingReadinessUploads,
  hasReadinessSaveChanges,
  READINESS_QUESTIONS_FORM_NAME,
  serializeReadinessResponses
} from "./sections/readinessQuestionsFormUtils";

const BackIcon = () => <svg height="16px" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" aria-hidden="true"><path fill="currentColor" d="M73.4 297.4C60.9 309.9 60.9 330.2 73.4 342.7L233.4 502.7C245.9 515.2 266.2 515.2 278.7 502.7C291.2 490.2 291.2 469.9 278.7 457.4L173.3 352L544 352C561.7 352 576 337.7 576 320C576 302.3 561.7 288 544 288L173.3 288L278.7 182.6C291.2 170.1 291.2 150.4 278.7 137.3C266.2 124.8 245.9 124.8 233.4 137.3L73.4 297.3z" /></svg>;

const getServerReadiness = (response) => {
  if (!response) return null;
  if (response.rescue_readiness_questions) return response;
  if (response.readiness) return response.readiness;
  if (response.emergency_readiness) return response.emergency_readiness;
  return null;
};

const buildPhotoPreviewItems = (questionEntries = [], filesPath = "") => {
  const items = [];

  questionEntries.forEach((questionEntry = {}, entryIndex) => {
    const question = questionEntry.question || {};
    if (!question.response_config || question.response_config.type !== "photo") {
      return;
    }

    const isWorkerScope = question.answer_scope === "worker";
    const candidates = isWorkerScope
      ? [
        { participant: questionEntry.supervisorParticipant, participantIndex: "supervisor", answer: questionEntry.supervisorAnswer },
        ...(Array.isArray(questionEntry.workerAnswers) ? questionEntry.workerAnswers.map((workerAnswer = {}) => ({
          participant: workerAnswer.participant,
          participantIndex: workerAnswer.participantIndex,
          answer: workerAnswer.answer
        })) : [])
      ]
      : [{ participant: questionEntry.supervisorParticipant, participantIndex: "supervisor", answer: questionEntry.supervisorAnswer }];

    candidates.forEach(({ participant, participantIndex, answer }) => {
      const fileUrl = formatAnswerValue(question, answer, filesPath);
      if (!fileUrl) {
        return;
      }

      items.push({
        key: `${question.id || entryIndex}-${participantIndex}`,
        fileUrl,
        questionLabel: `Q${entryIndex + 1} - ${question.question_text || ""}`,
        isWorkerScope,
        submittedBy: isWorkerScope ? resolveParticipantName(participant) : "",
        comment: answer && typeof answer.comment === "string" ? answer.comment.trim() : ""
      });
    });
  });

  return items;
};

export function ReadinessDocumentForm({ readiness, savedReadiness, sections = {}, change, reset, handleSubmit, onClose, context, hasPendingSaveChanges, hasBlockingUploads, onReadinessChange }) {
  const dispatch = useDispatch();
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [activeHeadingEditor, setActiveHeadingEditor] = useState(null);
  const checklist = getReadinessChecklist(readiness);
  const questions = useMemo(() => buildPreStartQuestionEntries(checklist), [checklist]);
  const filesPath = sections && sections.document_project_details && sections.document_project_details.data && sections.document_project_details.data.org_details
    ? sections.document_project_details.data.org_details.files_path || "" : "";
  const photoPreviewItems = useMemo(() => buildPhotoPreviewItems(questions, filesPath), [questions, filesPath]);
  const [activePhotoPreviewIndex, setActivePhotoPreviewIndex] = useState(-1);
  const photoPreviewIndexByKey = useMemo(() => photoPreviewItems.reduce((result, item, index) => {
    result[item.key] = index;
    return result;
  }, {}), [photoPreviewItems]);

  useEffect(() => {
    if (photoPreviewItems.length === 0) {
      setActivePhotoPreviewIndex(-1);
    } else if (activePhotoPreviewIndex >= photoPreviewItems.length) {
      setActivePhotoPreviewIndex(photoPreviewItems.length - 1);
    }
  }, [activePhotoPreviewIndex, photoPreviewItems]);

  const handleOpenPhotoPreview = (previewKey) => {
    if (previewKey && Object.prototype.hasOwnProperty.call(photoPreviewIndexByKey, previewKey)) {
      setActivePhotoPreviewIndex(photoPreviewIndexByKey[previewKey]);
    }
  };
  const handleClosePhotoPreview = () => setActivePhotoPreviewIndex(-1);
  const handlePreviousPhotoPreview = () => setActivePhotoPreviewIndex((index) => (
    photoPreviewItems.length ? (index - 1 + photoPreviewItems.length) % photoPreviewItems.length : -1
  ));
  const handleNextPhotoPreview = () => setActivePhotoPreviewIndex((index) => (
    photoPreviewItems.length ? (index + 1) % photoPreviewItems.length : -1
  ));
  const getHeading = (key) => {
    const contents = checklist && checklist.heading_contents && typeof checklist.heading_contents === "object"
      ? checklist.heading_contents
      : {};
    return typeof contents[key] === "string" ? contents[key] : "";
  };
  const openHeadingEditor = (fieldName, label, headingKey) => setActiveHeadingEditor({
    fieldName,
    label,
    value: getHeading(headingKey)
  });

  const handleDownload = () => {
    if (saving || generating || !savedReadiness || hasPendingSaveChanges) return Promise.resolve();

    setGenerating(true);
    return dispatch(generateJobShiftEmergencyReadinessPdf(
      context && context.jobId,
      context && context.shiftId,
      context && context.swmsId
    )).then((response = {}) => {
      if (!response.pdf_link) return Promise.reject(Strings.generic_error);
      return downloadDocument(response.pdf_link, true);
    }).catch((error) => {
      notification.error({
        message: Strings.error_title,
        description: typeof error === "string" ? error : Strings.file_download_error,
        className: "ant-error"
      });
    }).finally(() => setGenerating(false));
  };

  const onSubmit = (values) => {
    if (saving || !readiness || !hasPendingSaveChanges) return Promise.resolve();
    if (hasBlockingUploads) {
      notification.error({ message: Strings.error_title, description: "Wait for photo uploads to finish before saving.", className: "ant-error" });
      return Promise.resolve();
    }
    const payload = buildReadinessSavePayload(values, savedReadiness || readiness);
    if (!hasReadinessSaveChanges(payload)) return Promise.resolve();

    setSaving(true);
    return dispatch(saveJobShiftEmergencyReadiness(
      context && context.jobId,
      context && context.shiftId,
      context && context.swmsId,
      payload
    )).then((response) => {
      const serverReadiness = getServerReadiness(response);
      if (!serverReadiness) return Promise.reject(Strings.generic_error);
      if (onReadinessChange) onReadinessChange(serverReadiness);
      notification.success({ message: Strings.success_title, description: response.message || Strings.save_btn, className: "ant-success" });
    }).catch((error) => {
      notification.error({ message: Strings.error_title, description: typeof error === "string" ? error : Strings.network_error, className: "ant-error" });
    }).finally(() => setSaving(false));
  };

  return <form className={styles.viewPreview} onSubmit={handleSubmit(onSubmit)}>
    <div className={styles.PreviewPlatformBar}>
      <div><button className={styles.PreviewPlatformButton} onClick={onClose} type="button"><BackIcon /><span>Back</span></button></div>
      <div className={styles.PreviewPlatformBarRight}>
        {hasBlockingUploads ? <div className={styles.PreviewDirtyNotice}>Wait for photo uploads to finish before saving</div> : hasPendingSaveChanges ? <div className={styles.PreviewDirtyNotice}>Save your changes to download the Readiness document</div> : null}
        {hasPendingSaveChanges ? <button className={styles.PreviewPlatformButton} disabled={saving} onClick={reset} type="button"><span>Discard</span></button> : null}
        {saving ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : null}
        <button className={styles.PreviewPlatformButton} style={{ background: "#6366F1", color: "#fff", borderColor: "#6366F1" }} disabled={saving || !hasPendingSaveChanges || hasBlockingUploads} type="submit">
          <svg height="18px" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640">
            <path fill="currentColor" d="M160 96C124.7 96 96 124.7 96 160L96 480C96 515.3 124.7 544 160 544L480 544C515.3 544 544 515.3 544 480L544 237.3C544 220.3 537.3 204 525.3 192L448 114.7C436 102.7 419.7 96 402.7 96L160 96zM192 192C192 174.3 206.3 160 224 160L384 160C401.7 160 416 174.3 416 192L416 256C416 273.7 401.7 288 384 288L224 288C206.3 288 192 273.7 192 256L192 192zM320 352C355.3 352 384 380.7 384 416C384 451.3 355.3 480 320 480C284.7 480 256 451.3 256 416C256 380.7 284.7 352 320 352z" />
          </svg>
          <span>Save</span>
        </button>
        <button className={styles.PreviewPlatformButton} style={{ background: "#6366F1", color: "#fff", borderColor: "#6366F1" }} disabled={saving || generating || !savedReadiness || hasPendingSaveChanges} onClick={handleDownload} type="button">
          {generating ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : (
            <svg height="16px" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640">
              <path fill="currentColor" d="M128 128C128 92.7 156.7 64 192 64L341.5 64C358.5 64 374.8 70.7 386.8 82.7L493.3 189.3C505.3 201.3 512 217.6 512 234.6L512 512C512 547.3 483.3 576 448 576L192 576C156.7 576 128 547.3 128 512L128 128zM336 122.5L336 216C336 229.3 346.7 240 360 240L453.5 240L336 122.5zM303 505C312.4 514.4 327.6 514.4 336.9 505L400.9 441C410.3 431.6 410.3 416.4 400.9 407.1C391.5 397.8 376.3 397.7 367 407.1L344 430.1L344 344C344 330.7 333.3 320 320 320C306.7 320 296 330.7 296 344L296 430.1L273 407.1C263.6 397.7 248.4 397.7 239.1 407.1C229.8 416.5 229.7 431.7 239.1 441L303.1 505z" />
            </svg>
          )}
          <span>Download</span>
        </button>
      </div>
    </div>
    <div className={styles.PreviewWrap}><main><div className={styles.PreviewDoc}>
      <JobShiftReadinessViewHeader sections={sections} readiness={readiness} context={context} />
      <ReadinessDetails sections={sections} readinessData={readiness} />
      <ReadinessQuestionsBlock readinessData={checklist} questions={questions} formChange={change} filesPath={filesPath} disableHeadingEdit={saving} onOpenPhotoPreview={handleOpenPhotoPreview} onEditHeading={() => openHeadingEditor("readiness_checklist_heading", "Rescue Readiness Questions Heading", "rescue_readiness_checklist_heading")} />
      <ReadinessFlaggedIncorrectiveQuestions questions={questions} />
      <ReadinessSignOff readinessData={readiness} disableHeadingEdit={saving} onEditHeading={() => openHeadingEditor("readiness_staff_signoff", "Rescue Readiness Sign Off Heading", "rescue_readiness_staff_signoff")} />
    </div></main></div>
    <PreStartPhotoPreviewModal
      isOpen={activePhotoPreviewIndex >= 0}
      onClose={handleClosePhotoPreview}
      attachments={photoPreviewItems}
      currentIndex={activePhotoPreviewIndex}
      onNextClick={handleNextPhotoPreview}
      onPrevClick={handlePreviousPhotoPreview}
    />
    {activeHeadingEditor ? (
      <ReadinessHeadingEditorModal
        headingLabel={activeHeadingEditor.label}
        initialValues={{ heading_content: activeHeadingEditor.value }}
        onClose={() => setActiveHeadingEditor(null)}
        onSave={(value) => {
          change(activeHeadingEditor.fieldName, value || "");
          setActiveHeadingEditor(null);
        }}
      />
    ) : null}
  </form>;
}

const ReduxReadinessForm = reduxForm({ form: READINESS_QUESTIONS_FORM_NAME, enableReinitialize: true, destroyOnUnmount: true })(ReadinessDocumentForm);
const selector = formValueSelector(READINESS_QUESTIONS_FORM_NAME);
const ConnectedReadinessForm = connect((state, ownProps) => {
  const values = {
    responses: selector(state, "responses") || {},
    readiness_checklist_heading: selector(state, "readiness_checklist_heading"),
    readiness_staff_signoff: selector(state, "readiness_staff_signoff")
  };
  const savedReadiness = ownProps.readiness;
  const readiness = savedReadiness ? serializeReadinessResponses(values, savedReadiness) : savedReadiness;
  const payload = savedReadiness ? buildReadinessSavePayload(values, savedReadiness) : {};
  return {
    readiness,
    savedReadiness,
    hasPendingSaveChanges: hasReadinessSaveChanges(payload),
    hasBlockingUploads: savedReadiness ? hasBlockingReadinessUploads(values, savedReadiness) : false
  };
})(ReduxReadinessForm);

export default ConnectedReadinessForm;
